export default function RefundPolicyPage() {
  return (
    <main>
      <section className="relative h-[280px] md:h-[320px] flex items-center justify-center overflow-hidden bg-foreground-900">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900 to-foreground-900" />
        <div className="relative z-10 text-center px-4">
          <h1 className="text-3xl md:text-4xl font-bold text-white font-heading">
            Refund Policy
          </h1>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-white">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <p className="text-foreground-600 leading-relaxed mb-6">
              Effective Date: February 18, 2026
            </p>

            <p className="text-foreground-600 leading-relaxed mb-6">
              Thank you for purchasing or subscribing to services provided by Web 3.0 Soft Limited (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;). This Refund Policy explains the conditions under which refunds may be issued for purchases made through our websites, mobile applications, and other digital services (collectively, the &quot;Services&quot;).
            </p>

            <p className="text-foreground-600 leading-relaxed mb-6">
              Please read this policy carefully before making a purchase. By purchasing or subscribing to our Services, you agree to the terms of this Refund Policy. All payments are processed through Stripe, Inc. Refunds approved under this policy will be returned to the original payment method used at the time of purchase.
            </p>

            <h2 className="text-2xl font-bold text-foreground-900 mt-8 mb-4 font-heading">1. Refund Eligibility</h2>
            <p className="text-foreground-600 leading-relaxed mb-4">
              You may request a refund under the following general conditions:
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              <strong>1.1. Cancellation Within 14 Days of Initial Purchase</strong> You may request a full refund of the purchase amount if you cancel your subscription or service within 14 days of the original purchase date. Upon cancellation within this period, your refund will be processed to the original payment method via Stripe. Please note that Stripe&apos;s processing timelines may apply.
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              <strong>1.2. Technical Issues</strong> If you encounter a technical issue with the Service that cannot be resolved by our support team, you may be eligible for a refund. To qualify, you must provide sufficient information describing the issue so our team can reproduce and verify it. If the issue is confirmed and cannot be resolved, a refund may be issued.
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              <strong>1.3. Duplicate Purchase</strong> If you accidentally purchased the same service or subscription more than once, contact our support team for assistance and verification. Upon confirmation, a refund for the duplicate purchase may be issued.
            </p>

            <h2 className="text-2xl font-bold text-foreground-900 mt-8 mb-4 font-heading">2. How to Request a Refund</h2>
            <p className="text-foreground-600 leading-relaxed mb-4">
              To request a refund:
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              2.1. Contact our support team via email at support@app-for-security.com with the subject line &quot;Refund Request&quot;.
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              2.2. Include your full name, order or transaction ID (available in your Stripe payment confirmation email), date of purchase, and reason for the refund request.
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              2.3. We will review your request and respond within a reasonable timeframe.
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              Refund requests that do not include required details may be delayed or denied.
            </p>

            <h2 className="text-2xl font-bold text-foreground-900 mt-8 mb-4 font-heading">3. Refund Processing Time</h2>
            <p className="text-foreground-600 leading-relaxed mb-4">
              3.1. Once your refund request is approved, refunds are processed through Stripe and typically returned to your original payment method within 5–14 business days of approval.
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              3.2. Please note that depending on your bank or card issuer, it may take additional time for the refunded amount to appear in your account. Web 3.0 Soft Limited is not responsible for delays caused by your bank or Stripe&apos;s processing timelines.
            </p>

            <h2 className="text-2xl font-bold text-foreground-900 mt-8 mb-4 font-heading">4. Non-Refundable Situations</h2>
            <p className="text-foreground-600 leading-relaxed mb-4">
              Refunds will not be issued in the following circumstances, unless otherwise required by applicable law:
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              4.1. Requests made after the 14-day refund period.
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              4.2. Change of mind or dissatisfaction without qualifying technical issues.
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              4.3. Service usage beyond the initial period that already provided access to features or content.
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              4.4. Chargebacks or disputes initiated directly with your bank or card issuer without first contacting our support team.
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              4.5. Any refund requests in violation of applicable platform or Stripe policy rules.
            </p>

            <h2 className="text-2xl font-bold text-foreground-900 mt-8 mb-4 font-heading">5. Chargebacks</h2>
            <p className="text-foreground-600 leading-relaxed mb-4">
              5.1. We encourage you to contact our support team before initiating a chargeback with your bank or card issuer.
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              5.2. Unauthorized chargebacks may result in suspension of your account and access to the Services.
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              5.3. We reserve the right to contest chargebacks that do not meet the refund eligibility criteria outlined in this policy.
            </p>

            <h2 className="text-2xl font-bold text-foreground-900 mt-8 mb-4 font-heading">6. Subscription Cancellation</h2>
            <p className="text-foreground-600 leading-relaxed mb-4">
              6.1. You may cancel your subscription at any time through your account settings.
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              6.2. Cancellations will take effect at the end of your current billing period, and you will retain access to any paid features until that date.
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              6.3. Refund eligibility for partially unused periods is governed by the terms laid out above.
            </p>

            <h2 className="text-2xl font-bold text-foreground-900 mt-8 mb-4 font-heading">7. Contact Information</h2>
            <p className="text-foreground-600 leading-relaxed mb-4">
              For questions about refunds or to request a refund, please contact us:
            </p>
            <p className="text-foreground-600 leading-relaxed">
              Web 3.0 Soft Limited<br />
              85 Great Portland Street<br />
              Westminster, W1W 7LT<br />
              United Kingdom<br />
              Phone: +44 7441 424350<br />
              Email: support@app-for-security.com
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
